import React, { useEffect, useState } from 'react';
import MoonLoader from 'react-spinners/MoonLoader';
import useUsers from '../../store/users/usersContext';
import { useHistory } from 'react-router';
import { COLORS } from '../../config';
import { COUNTRIES } from '../../helpers/selectOptions';
import FieldWithEdit from '../../components/FieldItems/FieldWithEdit';
import EditIcon from '../../components/Icons/outline/EditIcon';
import ProfilePicture from '../../components/App/ProfilePicture';
import PrimaryButton from '../../components/Buttons/PrimaryButton';
import FieldWithSelectEdit from '../../components/FieldItems/FieldWithSelectEdit';
import ClosedLockIcon from '../../components/Icons/solid/ClosedLockIcon';
import RedButton from '../../components/Buttons/RedButton';
import GreenButton from '../../components/Buttons/GreenButton';
import DeleteUserModal from '../modals/DeleteUserModal';
import dayjs from 'dayjs';
import SecondaryButton from '../../components/Buttons/SecondaryButton';
import UpdateMedicalQuestionnaire from '../account/UpdateMedicalQuestionnaire';

function ViewPatientDetails({
  patientId : id
}) {

  const history = useHistory();

  const {
    getUserDetails,
    updateUserById,
    deleteUser,
    forceDeleteUser,
    activateUser,
  } = useUsers();

  const [isLoading, setIsLoading] = React.useState(true);

  const [patient, setPatient] = React.useState({});
  const [edit, setEdit] = React.useState(false);
  const [viewQuestionnaire, setViewQuestionnaire] = useState(false)

  useEffect(() => {
    (async function fetchDetails() {
      await getUserDetails(id).then(res => {
        setPatient(res?.data);
        setIsLoading(false);
      }).catch(() => {
        history.push('/manage-patients')
      })
    })();
  }, [id])
  

  const [userEdits, _setUserEdits] = useState();

  const setUserEdits = (newVal) => _setUserEdits({ ...userEdits, ...newVal });

   const [showDeleteUserModal, setShowDeleteUserModal] = React.useState(false);

  const handleUpdateClick = () => {
    updateUserById(patient._id, userEdits).then(res => {
      setPatient(res.data);
    });
    setEdit(false);
  }

  const handleEditClick = (field) => {
    switch (field) {
      case "personalInfo":
        _setUserEdits({
          first_name: patient.first_name,
          last_name: patient.last_name,
        });
      break;
      case 'contactInfo':
        _setUserEdits({
          location: {
            address: patient.location.address,
            postal_code: patient.location.postal_code,
            city: patient.location.city,
            country: patient.location.country,
          },
          mobile_nb: patient.mobile_nb
        })
      break;
      default:
        return;
    }
    if (edit === field) setEdit(false)
    if (edit !== field) setEdit(field)
  }
  
  const handleDelete = () => {
    if (patient.active) {
      deleteUser(patient._id).then(() => history.push('/manage-patients'));
    }
    if (!patient.active) {
      forceDeleteUser(patient._id).then(() => history.push('/manage-patients'));
    }
  }

  const handleActivate = () => {
    activateUser(patient._id).then(() => history.push('/manage-patients'));
  }

  const countryOptions = [];
  if (!countryOptions.length) COUNTRIES.forEach(country => {
    countryOptions.push({
      value: country,
      label: country
    })
  });

  if (isLoading) return (
    <div className='flex justify-around items-center h-full'>
      <MoonLoader color={COLORS.colorPrimary}/>
    </div>
  )

  return (
    <div className="flex flex-col">
      <DeleteUserModal showDeleteUserModal={showDeleteUserModal} setShowDeleteUserModal={setShowDeleteUserModal} onDelete={handleDelete} forceDelete={patient?.active? false : true}/>
      <UpdateMedicalQuestionnaire
        setShowUpdateMedicalQuestionnaire={ (val)=>setViewQuestionnaire(val) }
        showUpdateMedicalQuestionnaire={viewQuestionnaire}
        patientInfo={patient.patientInfo}
        viewOnly={true}
      />
      <div className="bg-white rounded shadow py-12 px-10">
        <div className='flex justify-between items-center'>
          <h1 className="mb-10 text-2xl text-secondary">Personal Information</h1>
          <div className={`${edit === 'personalInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'personalInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('personalInfo')} className='cursor-pointer'/>
          </div>
        </div>
        <div className='grid grid-cols-1 gap-y-4'>  
          <FieldWithEdit label='First name' data={edit === 'personalInfo' ? userEdits.first_name : patient.first_name} onChange={(e) => setUserEdits({first_name: e.target.value})} edit={edit === 'personalInfo'}/>
          <FieldWithEdit label='Last name' data={edit === 'personalInfo' ? userEdits.last_name : patient.last_name} onChange={(e) => setUserEdits({last_name: e.target.value})} edit={edit === 'personalInfo'}/>
          <FieldWithEdit locked label='Email' data={patient.email} edit={edit === 'personalInfo'}/>
          <FieldWithEdit 
            type='date' 
            label='Date of Birth' 
            locked
            data={patient.date_of_birth ? dayjs(patient.date_of_birth).format('DD/MM/YYYY') : 'n/a'}
            edit={edit === 'personalInfo'}
          />
          <FieldWithEdit override label='Profile picture'>
            <div className="flex items-center mt-1 sm:mt-0 justify-between w-full">
              <ProfilePicture user={patient}/>
              {edit === 'personalInfo' && 
                <ClosedLockIcon fill={COLORS.colorSecondary} className='mr-3'/>
              }
            </div>

          </FieldWithEdit>
          <FieldWithEdit locked data={patient.verified ? 'Account verified' : 'Account not verified'} label={'Verified'} edit={edit === 'personalInfo'}/>
          {edit === 'personalInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Save' onClick={handleUpdateClick} />
            </div>
          </div>}
        </div>
      </div>
      <div className="bg-white rounded shadow py-12 px-10 mt-6">
        <div className='flex justify-between items-center'>
          <h1 className="mb-10 text-2xl text-secondary">Contact details</h1>
          <div className={`${edit === 'contactInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'contactInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('contactInfo')} className='cursor-pointer'/>
          </div>
        </div>
        <div className='grid grid-cols-1 gap-y-4'>  
          <FieldWithSelectEdit label='Country' options={countryOptions} data={edit === 'contactInfo' ? userEdits.location.country : patient.location.country} onChange={(e) => setUserEdits({location: {...userEdits.location, country: e.value}})} edit={edit === 'contactInfo'}/> 
          <FieldWithEdit label='City' data={edit === 'contactInfo' ? userEdits.location.city : patient.location.city} onChange={(e) => setUserEdits({location: { ...userEdits.location, city: e.target.value }})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Address' data={edit === 'contactInfo' ? userEdits.location.address : patient.location.address} onChange={(e) => setUserEdits({ location: { ...userEdits.location, address: e.target.value }})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Postal code' data={edit === 'contactInfo' ? userEdits.location.postal_code : patient.location.postal_code} onChange={(e) => setUserEdits({ location: { ...userEdits.location, postal_code: e.target.value }})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Mobile' data={edit === 'contactInfo' ? userEdits.mobile_nb : patient.mobile_nb} onChange={(e) => setUserEdits({mobile_nb: e.target.value})} edit={edit === 'contactInfo'}/>
          {edit === 'contactInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Save' onClick={handleUpdateClick} />
            </div>
          </div>}
        </div>
      </div>
      {patient.user_type === 'patient' && 
      <>
        <div className="bg-white rounded shadow py-12 px-10 mt-6">
          <div className='flex justify-between items-center mb-10 '>
            <h1 className="text-2xl text-secondary">Medical questionnaire</h1>
            {patient?.patientInfo?.completed && <SecondaryButton text='View details' onClick={() => setViewQuestionnaire(true)} />}
          </div>
          <div className='grid grid-cols-1 gap-y-4'>  
            <FieldWithEdit locked label='Status' data={patient?.patientInfo?.completed ? 'Medical questionnaire is up to date' : "Medical questionnaire is not completed"}/>
          </div>
        </div>
      </>
      }
      <div className='w-full flex flex-row-reverse pb-4'>
        <PrimaryButton className="w-32 mt-8" text='Back' onClick={() => history.push('/manage-patients')} />
        <RedButton className="w-32 mr-4 mt-8" text={patient?.active ? 'Delete user' : 'Erase user'} onClick={() => setShowDeleteUserModal(true)} />
        {!patient.active && <GreenButton className="w-32 mr-4 mt-8" text={'Activate user'} onClick={handleActivate} />}
      </div>
    </div>
  )
}

export default ViewPatientDetails
